import React, { useEffect, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { trim } from 'lodash';
import isEmail from 'validator/lib/isEmail';
import { SButton } from '@chargebee/sting-react';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import {
  ImportantMessage,
  InputError,
  InputLabel,
} from '@bbkAdminComponents/form-components';
import { useInfoModal } from '@bbkAdminComponents/info-dialog';
import { PreparingOverlay } from '@bbkAdminComponents/preparing-overlay/preparing-overlay';
import img from '@bbkAdminComponents/trial-signup/assets/signup-page1-cb-retention.png';
import stephano from '@bbkAdminComponents/trial-signup/assets/stephano.png';
import { TrialSignupLogo } from '@bbkAdminComponents/trial-signup/TrialSignupLogo';
import { TrialSignupSteps } from '@bbkAdminComponents/trial-signup/TrialSignupSteps';
import {
  domainValidator,
  extractDomain,
  MARKETO_UTM_FIELDS_QUERY_PARAMS_TO_COOKIE,
  preauthCompanySlice,
  type MarketoUtmFieldsType,
} from '@bbkAdminRedux/rtkq/preauth-company.slice';
import {
  parseRTKAPIError,
  type RTKAPIErrorResponse,
} from '@bbkAdminRedux/rtkq/rtkq-error-logger';
import { encodeObjectForQuery } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { SignupPaths } from '@bbkAdminRouter/signup-paths';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { useSearchParams } from '@bbkAdminUtils/useSearchParams';
import { useHistory } from '@bbkShared/react-router';

const TrialSignupPage1Testimonial: React.FC = () => (
  <div>
    <div>
      <div className="tw-text-3xl">
        <strong>
          &quot;With Chargebee we were able to reduce churn by 20% nearly
          instantly after deployment.
        </strong>{' '}
        When compared to building an internal solution... Chargebee Growth was
        the obvious choice.&quot;
      </div>
      <div className="tw-mb-10 tw-mt-6 tw-flex tw-text-sm">
        <img
          className="tw-mr-5"
          srcSet={`${stephano} 2x`}
          alt="Stefano Scalia, Head of Growth pliability"
        />
        <div>
          - Stefano Scalia, Head of Growth
          <br />
          <strong>pliability</strong>
        </div>
      </div>
    </div>
    <img srcSet={`${img} 2x`} className="tw-w-11/12" alt="Testimonials" />
  </div>
);

export const signupInputLabelClassName = '!tw-m-0 !tw-mt-2 !tw-text-black';
export const signupSelectLabelClassName =
  '!tw-m-0 !tw-mt-4 !tw-mb-2 !tw-text-black';

type FormData = {
  companyName: string;
  domain: string;
  email: string;
};

type FormTouched = Partial<{
  companyName: boolean;
  domain: boolean;
  email: boolean;
}>;

type FormError = Partial<{
  companyName: string;
  domain: string;
  email: string;
}>;

export const TrialSignupPage: React.FC = () => {
  const history = useHistory();
  const searchParams = useSearchParams<
    {
      cb_email: string;
    } & MarketoUtmFieldsType
  >();
  const opCbToken = searchParams.cb_email || '';
  const cbToken = isEmail(opCbToken) ? opCbToken : undefined;
  const {
    data: chargebeeCachedData,
    isFetching: isFetchingCBData,
    isError,
  } = preauthCompanySlice.endpoints.fetchCachedDataFromChargebee.useQuery(
    cbToken ? { token: cbToken } : skipToken
  );
  const isCbIdpPhase5Enabled = useFeatureFlags(
    FeatureFlagsE.CHARGEBEE_IDP_PHASE_5
  );
  const { triggerModal } = useInfoModal();
  const [cacheCompanyCreation$] =
    preauthCompanySlice.endpoints.cacheCompanyCreation.useMutation();
  const [initiateProvisioning$] =
    preauthCompanySlice.endpoints.initiateProvisioning.useMutation();

  const [showOverLay, setShowOverLay] = useState<boolean>(!!cbToken);

  // gitleaks:ignore -- not a real token, is just the email
  const shouldUseTwoSteps = cbToken || isCbIdpPhase5Enabled;

  const gotoNonCbSignup = () => {
    history.push({
      pathname: SignupPaths.trialSignup,
    });
  };

  const gotoSignupPage2 = (s: string) =>
    history.push({
      pathname: SignupPaths.trialSignup2,
      search: s,
    });

  useEffect(() => {
    if (isError) {
      setShowOverLay(false);
      gotoNonCbSignup();
    }
    if (chargebeeCachedData) {
      // on cb path
      if (
        chargebeeCachedData.simplified_flow ||
        chargebeeCachedData.company_exists_for_merchant_id
      ) {
        // simplified flow or company already exists with merchantID
        gotoSignupPage2(
          encodeObjectForQuery({
            ...searchParams,
            companyName: chargebeeCachedData.merchant_name,
            domain: extractDomain(chargebeeCachedData.domain),
          })
        );
      } else {
        setShowOverLay(false);
      }
    }
  }, [isFetchingCBData, isError]);

  const [formTouched, setFormTouched] = useState<FormTouched>({});
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    domain: '',
    email: '',
  });
  const [formError, setFormError] = useState<FormError>({});

  const validateCompanyName = (): string => {
    const { companyName } = formData;

    let companyNameError = '';
    if (formTouched.companyName) {
      if (!companyName || trim(companyName).length === 0) {
        companyNameError = 'Please enter your company name.';
      } else if (companyName.length < 3) {
        companyNameError = 'Company name should be at least 3 characters long.';
      }
    }
    return companyNameError;
  };

  const autoValidateFields = (): boolean => {
    const companyNameError = validateCompanyName();

    setFormError({
      companyName: companyNameError,
      domain: '',
    });

    return !companyNameError;
  };

  const validateFields = (): boolean => {
    /** ABK: Replaced Formik+Yup validations with manual setup only for step 1 to
     * accommodate Chargebee integration since enableReinitialize property of Formik
     * was not working for me. */
    const companyNameError = validateCompanyName();
    const { domain, email } = formData;
    const domainError = domainValidator(domain);
    let emailError =
      isCbIdpPhase5Enabled && !isEmail(email)
        ? 'Please enter a valid email.'
        : '';
    setFormError({
      companyName: companyNameError,
      domain: domainError,
      email: emailError,
    });

    return !companyNameError && !domainError && !emailError;
  };

  useEffect(() => {
    autoValidateFields();
  }, [formData]);

  useEffect(() => {
    if (!chargebeeCachedData) return;
    setFormTouched({
      companyName: true,
      domain: true,
      email: true,
    });
    setFormData({
      companyName: chargebeeCachedData.merchant_name,
      domain: extractDomain(chargebeeCachedData.domain) ?? '',
      email: chargebeeCachedData.email,
    });
  }, [chargebeeCachedData]);

  const onSubmit = async () => {
    if (!validateFields()) {
      BlackToastTrigger({
        content: 'There are unresolved validation errors on this page.',
      });
      return;
    }
    const { domain, email, companyName } = { ...formData };

    if (isCbIdpPhase5Enabled && !cbToken) {
      try {
        setShowOverLay(true);
        await cacheCompanyCreation$({
          email: email,
          company_name: companyName,
          domain: domain,
        })
          .unwrap()
          .catch((e) => {
            console.error(e);
            throw new Error('Cache company creation failed');
          });

        initiateProvisioning$({ domain, email, company: companyName })
          .unwrap()
          .then((response) => {
            if (response.valid) {
              gotoSignupPage2(
                encodeObjectForQuery({
                  ...searchParams,
                  ...formData,
                  cb_email: email,
                })
              );
            } else {
              triggerModal({
                title: <>Complete Account Setup</>,
                description: (
                  <>We’ve sent an email to {email} to complete your setup.</>
                ),
              });
            }
          })
          .catch((e: RTKAPIErrorResponse) => {
            const err = parseRTKAPIError(e);
            if (e?.status == 400) {
              BlackToastTrigger({
                content: 'Provision email was sent.',
              });
            } else {
              BlackToastTrigger({
                content: 'Failed to initiate company creation.',
              });
              GlobalLogger.error(`initiateProvisioning error: ${err?.message}`);
            }
          });
      } catch (err) {
        const e = err as Error;
        GlobalLogger.error(
          `Sign up error: failed to cache company creation. ${e}`
        );
        BlackToastTrigger({ content: 'Sign up failed.' });
        setShowOverLay(false);
      }
    } else {
      gotoSignupPage2(
        encodeObjectForQuery({
          ...searchParams,
          ...formData,
        })
      );
    }

    const cookieExpiration = new Date(Date.now() + 30 * 60 * 1000); // 30minutes

    Object.keys(MARKETO_UTM_FIELDS_QUERY_PARAMS_TO_COOKIE).forEach((key) => {
      const cookieName =
        MARKETO_UTM_FIELDS_QUERY_PARAMS_TO_COOKIE[
          key as keyof typeof MARKETO_UTM_FIELDS_QUERY_PARAMS_TO_COOKIE
        ].cookieName;
      const utmField = searchParams[key as keyof typeof searchParams];
      if (utmField) {
        document.cookie = `${cookieName}=${encodeURIComponent(utmField)}; expires=${cookieExpiration.toUTCString()}; path=/;`;
      }
    });
  };

  return (
    <div className="trial-signup-page">
      <div className="left-col tw-mx-auto">
        <div>
          <div className="tw-text-center">
            <TrialSignupLogo />
            <div className="page-title">Create your company</div>
          </div>
          <InputLabel className={signupInputLabelClassName}>
            Company <ImportantMessage>*</ImportantMessage>
          </InputLabel>
          <input
            name="companyName"
            type="text"
            value={formData.companyName}
            onChange={(e) => {
              setFormTouched((prev) => ({ ...prev, companyName: true }));
              setFormData((prev) => ({ ...prev, companyName: e.target.value }));
            }}
            className="bbk-simple-text-input tw-w-full"
          />
          <InputError>{formError.companyName}</InputError>

          <InputLabel className={signupInputLabelClassName}>
            Domain <ImportantMessage>*</ImportantMessage>
          </InputLabel>
          <input
            name="domain"
            placeholder="www.yourcompany.com"
            type="text"
            value={formData.domain}
            className="bbk-simple-text-input tw-w-full"
            onChange={(e) => {
              if (!formTouched.domain) {
                setFormTouched((prev) => ({ ...prev, domain: true }));
              }
              setFormData((prev) => ({ ...prev, domain: e.target.value }));
            }}
          />
          <InputError>{formError.domain}</InputError>

          {isCbIdpPhase5Enabled && !cbToken && (
            <>
              <InputLabel className={signupInputLabelClassName}>
                Email <ImportantMessage>*</ImportantMessage>
              </InputLabel>
              <input
                name="email"
                type="text"
                value={formData.email}
                className="bbk-simple-text-input tw-w-full"
                onChange={(e) => {
                  if (!formTouched.email) {
                    setFormTouched((prev) => ({ ...prev, email: true }));
                  }
                  setFormData((prev) => ({ ...prev, email: e.target.value }));
                }}
              />
              <InputError>{formError.email}</InputError>
            </>
          )}

          <SButton
            variant="primary"
            fullWidth
            className="tw-mb-4 tw-mt-2"
            onClick={onSubmit}
          >
            Next
          </SButton>
        </div>

        <TrialSignupSteps step={1} maxSteps={shouldUseTwoSteps ? 2 : 3} />
      </div>
      {showOverLay && <PreparingOverlay />}
    </div>
  );
};
